import React, { useEffect, useMemo, useState } from 'react';
import { FileSpreadsheet, RefreshCw, Search } from 'lucide-react';
import api from '../lib/api';
import { formatIST } from '../lib/formatDate';

interface SheetDataViewerProps {
  apiPath: string;
  label: string;
  changesApiPath?: string;
}

interface SheetChange {
  tab: string; change_type: 'added' | 'removed' | 'updated'; row_label: string; column: string | null;
}

export default function SheetDataViewer({ apiPath, label, changesApiPath }: SheetDataViewerProps) {
  const [tabs, setTabs] = useState<Record<string, string[][]>>({});
  const [connected, setConnected] = useState(false);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [changes, setChanges] = useState<SheetChange[]>([]);

  const load = () => {
    setLoading(true);
    api.get(apiPath)
      .then((res) => {
        setConnected(res.data.connected);
        setTabs(res.data.tabs || {});
        setSyncedAt(res.data.synced_at);
        const tabNames = Object.keys(res.data.tabs || {});
        setActiveTab((prev) => (prev && tabNames.includes(prev) ? prev : tabNames[0] || null));
      })
      .catch(() => setConnected(false))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    if (changesApiPath) {
      api.get(changesApiPath).then((res) => setChanges(res.data.changes || [])).catch(() => setChanges([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiPath]);

  useEffect(() => {
    setQuery('');
  }, [activeTab]);

  const tabNames = Object.keys(tabs);
  const rows = activeTab ? tabs[activeTab] || [] : [];
  const headerRow = rows[0] || [];
  const dataRows = rows.slice(1);
  // some tabs have data rows wider than the header row — never truncate real cells
  const colCount = Math.max(headerRow.length, ...dataRows.map((r) => r.length), 1);

  // Cross-reference the diff-derived change log against the currently rendered tab, so cells
  // that changed on the last sync get a visible highlight right in the raw grid — matched by
  // row label (first non-empty cell, same identity rule the backend diff uses) + column name.
  const updatedCellKeys = useMemo(() => {
    const set = new Set<string>();
    changes.filter((c) => c.tab === activeTab && c.change_type === 'updated' && c.column).forEach((c) => set.add(`${c.row_label}|${c.column}`));
    return set;
  }, [changes, activeTab]);
  const addedRowLabels = useMemo(() => {
    const set = new Set<string>();
    changes.filter((c) => c.tab === activeTab && c.change_type === 'added').forEach((c) => set.add(c.row_label));
    return set;
  }, [changes, activeTab]);

  const filteredRows = useMemo(() => {
    if (!query.trim()) return dataRows;
    const q = query.toLowerCase();
    return dataRows.filter((row) => row.some((cell) => (cell || '').toLowerCase().includes(q)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataRows, query]);

  if (loading) {
    return (
      <div data-testid="sheet-viewer-loading" className="bg-white border border-slate-200/80 rounded-3xl p-6 shadow-sm text-xs text-slate-400 font-mono">
        Loading live {label} data…
      </div>
    );
  }

  if (!connected) {
    return (
      <div data-testid="sheet-viewer-disconnected" className="bg-white border border-slate-200/80 rounded-3xl p-6 shadow-sm flex items-center gap-3 text-xs text-slate-500">
        <FileSpreadsheet className="h-4 w-4 text-slate-400" />
        <span>Google Sheets not connected yet. Ask your admin to connect it from the Admin Console → Integrations tab.</span>
      </div>
    );
  }

  return (
    <div data-testid="sheet-data-viewer" className="bg-white border border-slate-200/80 rounded-3xl p-6 shadow-sm space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-100">
        <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">Live Google Sheet — {label}</span>
        <div className="flex items-center gap-2">
          <span className="text-[9px] font-mono bg-emerald-50 text-emerald-700 px-2.5 py-0.5 rounded-full border border-emerald-100 uppercase font-bold">
            Synced {formatIST(syncedAt)}
          </span>
          <button
            data-testid="sheet-viewer-refresh"
            onClick={load}
            title="Refresh view"
            className="p-1.5 rounded-lg border border-slate-200 text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <RefreshCw className="h-3 w-3" />
          </button>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {tabNames.map((name) => (
          <button
            key={name}
            data-testid={`sheet-tab-${name}`}
            onClick={() => setActiveTab(name)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-[10.5px] font-semibold whitespace-nowrap border transition-colors ${
              activeTab === name
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
            }`}
          >
            {name}
          </button>
        ))}
      </div>

      {changesApiPath && (updatedCellKeys.size > 0 || addedRowLabels.size > 0) && (
        <div className="flex items-center gap-4 text-[10px] font-mono text-slate-400">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-amber-50 ring-1 ring-inset ring-amber-300 inline-block" />Changed since last sync</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-emerald-50 inline-block" />New row</span>
        </div>
      )}

      {rows.length === 0 ? (
        <p data-testid="sheet-tab-empty" className="text-xs text-slate-400 font-mono">This tab has no rows yet.</p>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                data-testid="sheet-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search within ${activeTab}…`}
                className="w-full bg-slate-50 border border-slate-200 pl-8 pr-3 py-2 text-xs rounded-xl focus:outline-none focus:border-slate-400 text-slate-700"
              />
            </div>
            <span data-testid="sheet-row-count" className="text-[10px] font-mono text-slate-400 font-semibold whitespace-nowrap">
              Showing {filteredRows.length.toLocaleString()} of {dataRows.length.toLocaleString()} rows
            </span>
          </div>

          <div className="overflow-auto max-h-[560px] border border-slate-100 rounded-2xl">
            <table className="w-full text-left text-xs text-slate-600">
              <thead className="sticky top-0 bg-white z-10 shadow-[0_1px_0_0_rgba(0,0,0,0.05)]">
                <tr className="border-b border-slate-100 text-[10.5px] text-slate-400 font-mono font-bold">
                  {Array.from({ length: colCount }).map((_, i) => (
                    <th key={i} className="py-3 px-4 whitespace-nowrap">{headerRow[i] || `COL ${i + 1}`}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-sans">
                {filteredRows.map((row, ri) => {
                  const rowLabel = row.find((c) => c) || `Row ${ri + 2}`;
                  const isNewRow = addedRowLabels.has(rowLabel);
                  return (
                    <tr key={ri} className={`hover:bg-slate-50/50 transition-colors ${isNewRow ? 'bg-emerald-50/60' : ''}`}>
                      {Array.from({ length: colCount }).map((_, ci) => {
                        const isChangedCell = updatedCellKeys.has(`${rowLabel}|${headerRow[ci]}`);
                        return (
                          <td key={ci} className={`py-2.5 px-4 whitespace-nowrap ${isChangedCell ? 'bg-amber-50 ring-1 ring-inset ring-amber-300 font-semibold text-amber-800' : ''}`}>
                            {row[ci] ?? ''}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
